import type { Application } from 'pixi.js';
import type { FpsMeter } from '../debug/fpsMeter';
import type { StageScaler } from '../render/StageScaler';

export interface SceneContext {
  app: Application;
  scaler: StageScaler;
  uiRoot: HTMLElement;
  fps: FpsMeter | null;
  scenes: SceneManager;
}

export interface Scene {
  enter(ctx: SceneContext): void;
  exit(): void;
  frame(nowMs: number): void;
  /** Called when another scene is pushed on top / popped back off. */
  suspend?(): void;
  resume?(): void;
}

/**
 * Minimal scene stack — the one architectural divergence from BoomTown.
 * A single app ticker delegates to the top scene; covered scenes freeze.
 */
export class SceneManager {
  ctx!: SceneContext;
  private readonly stack: Scene[] = [];

  push(scene: Scene): void {
    this.top()?.suspend?.();
    this.stack.push(scene);
    scene.enter(this.ctx);
  }

  pop(): void {
    const leaving = this.stack.pop();
    leaving?.exit();
    this.top()?.resume?.();
  }

  replace(scene: Scene): void {
    const leaving = this.stack.pop();
    leaving?.exit();
    this.stack.push(scene);
    scene.enter(this.ctx);
  }

  top(): Scene | undefined {
    return this.stack[this.stack.length - 1];
  }

  get depth(): number {
    return this.stack.length;
  }

  frame(nowMs: number): void {
    this.top()?.frame(nowMs);
  }
}
